import { useContext, useEffect, useRef, useState } from "react";
import { ExploreContext } from "context/ExploreContext";
import { usePremium } from "context/PremiumContext";
import { retrieveToken, storeToken } from "utils/storage";
import GoPremiumModal from "components/CustomWidgets/goPremiumModal";
import Tabs from "components/BaseWidgets/tab";
import { useScrollRestoration } from "hooks/api/useScrollRestoration";
import {
  ExploreLatest,
  ExploreMoreMatches,
  ExploreMyMatches,
  ExploreNearMe,
  ExplorePremium,
  ExploreRecommended,
  FilterIcon,
} from "assets/icons";
import Filter from "./components/Filter";
import ExploreLayout from "./components/ExploreLayout/index";
import CardLists from "./components/CardLists";
import MoreMatches from "./components/MoreMatches/index";
import "./style.scss";
import {
  useCareerMatches,
  useMyMatches,
  useNearMeMatches,
  useProfessionalMatches,
  useRecentlyViewed,
} from "hooks/api/useExplore";
import { useLatestProfile, usePremiumMatches } from "hooks/api/useHome";
import { useSimiliarMatches } from "hooks/api/useProfile";
import { selectedListData } from "./helper";
import { CardLists as PaginationCard } from "./../Engage/components/CardLists";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "components/BaseWidgets/button";

function Explore() {
  const {
    clearAll,
    setLoading,
    tabActive,
    setTabActive,
    selectedList,
    setSelectedList,
    filterData,
  } = useContext(ExploreContext);
  useScrollRestoration(tabActive);
  const { isPremium } = usePremium();
  const location = useLocation();
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState(1);
  const [upgradeModal, setUpgradeModal] = useState(() => {
    if (isPremium) return false;
    return retrieveToken("gopremiummodal") !== "true";
  });
  const [searchParams] = useSearchParams();
  const prevTabRef = useRef(tabActive);
  const initialMountRef = useRef(true);
  const tabParamHandled = useRef(false);
  const [similarProfileIdState, setSimilarProfileIdState] = useState(
    () => searchParams.get("profile_id") || null
  );
  const isFilterVisible =
    tabActive !== "6" &&
    (tabActive !== "7" || [1, 2, 3].includes(selectedList));

  // Handles tab and selectedList restoration on mount. Runs once via restoredRef.
  // 1. If URL has a "tab" param (e.g., navigating from PersonProfile "See All"),
  //    skip restore/reset — the tab-param useEffect below will handle it.
  // 2. If returning from a profile detail page (back navigation),
  //    restore the previously saved tab and selectedList from sessionStorage.
  // 3. Otherwise (fresh visit), reset to default tab "1" and clear all filters.
  const restoredRef = useRef(false);

  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;
    const currentKey = location.key;
    const lastKey = sessionStorage.getItem("exploreLastKey");

    if (!restoredRef.current) {
      const hasTabParam = searchParams.get("tab");
      const isBackNavigation = isFromProfileDetail || currentKey === lastKey;

      if (!hasTabParam) {
        if (isBackNavigation) {
          const savedTab = sessionStorage.getItem("exploreActiveTab");
          const savedList = sessionStorage.getItem("exploreSelectedList");

          if (savedTab) {
            setTabActive(savedTab);
          }
          if (savedList) {
            setSelectedList(parseInt(savedList));
          }
        } else {
          setTabActive("1");
          clearAll();
          setSelectedList(0);
          sessionStorage.removeItem("exploreActiveTab");
          sessionStorage.removeItem("exploreSelectedList");
        }
      }
      restoredRef.current = true;
    }

    sessionStorage.setItem("exploreLastKey", currentKey);
  }, [location.key, location.state?.fromProfileDetail]);

  // Read tab from URL param once, then remove it from URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && !tabParamHandled.current) {
      tabParamHandled.current = true;
      setTabActive(tabParam);
      clearAll();

      // Preserve profile_id for similar matches before stripping URL params
      const profileId = searchParams.get("profile_id");
      if (profileId) {
        setSimilarProfileIdState(profileId);
        setSelectedList(4);
      }

      // Remove params from URL so they don't re-trigger on tab switches
      navigate("/explore", { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (isPremium) return;

    const interval = setInterval(
      () => {
        setUpgradeModal(true);
      },
      10 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [isPremium]);

  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;

    if (initialMountRef.current) {
      if (!isFromProfileDetail) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      initialMountRef.current = false;
    }

    if (!isFromProfileDetail) {
      setPagination(1);
    }
  }, [location.state?.fromProfileDetail]);

  useEffect(() => {
    if (prevTabRef.current !== tabActive) {
      const isFromProfileDetail = location.state?.fromProfileDetail === true;

      // Close filter when switching tabs
      setShowFilter(false);

      if (!isFromProfileDetail) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setPagination(1);
      }
      prevTabRef.current = tabActive;
    }
  }, [tabActive, location.state?.fromProfileDetail]);

  useEffect(() => {
    if (tabActive === "7") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pagination]);

  const { data: myMatchesData, isLoading: isMyMatchesLoading } = useMyMatches(
    {
      page: pagination,
      limit: 20,
      see_all: true,
      ...(filterData && Object.keys(filterData).length > 0
        ? { filter_data: JSON.stringify(filterData) }
        : {}),
    },
    {
      enabled: tabActive === "1" || tabActive === "5",
    },
  );

  const { data: nearMeMatches, isLoading: isNearMeLoading } = useNearMeMatches(
    {
      page: pagination,
      limit: 20,
      see_all: true,
      ...(filterData && tabActive === "2"
        ? { filter_data: JSON.stringify(filterData) }
        : {}),
    },
    {
      enabled: tabActive === "2",
    },
  );

  const { data: latest, isLoading: islatestLoading } = useLatestProfile(
    {
      page: pagination,
      limit: 20,
      see_all: true,
      ...(filterData && tabActive === "3"
        ? { filter_data: JSON.stringify(filterData) }
        : {}),
    },
    {
      enabled: tabActive === "3",
    },
  );

  const { data: premiumMatches, isLoading: isPremiumLoading } =
    usePremiumMatches(
      {
        page: pagination,
        limit: 20,
        see_all: true,
        ...(filterData && tabActive === "4"
          ? { filter_data: JSON.stringify(filterData) }
          : {}),
      },
      {
        enabled: tabActive === "4",
      },
    );

  const { data: recent, isLoading: isRecentLoading } = useRecentlyViewed(
    {
      page: pagination,
      limit: 8,
      see_all: true,
      ...(selectedList === 1
        ? { filter_data: JSON.stringify(filterData) }
        : {}),
    },
    {
      enabled: tabActive === "6" || (tabActive === "7" && selectedList === 1),
    },
  );

  const { data: careerMatchesData, isLoading: careerMatchesLoading } =
    useCareerMatches(
      {
        page: pagination,
        limit: 8,
        see_all: true,
        ...(selectedList === 2
          ? { filter_data: JSON.stringify(filterData) }
          : {}),
      },
      {
        enabled: tabActive === "6" || (tabActive === "7" && selectedList === 2),
      },
    );

  const {
    data: professionalMatchesData,
    isLoading: professionalMatchesLoading,
  } = useProfessionalMatches(
    {
      page: pagination,
      limit: 8,
      see_all: true,
      ...(selectedList === 3
        ? { filter_data: JSON.stringify(filterData) }
        : {}),
    },
    {
      enabled: tabActive === "6" || (tabActive === "7" && selectedList === 3),
    },
  );

  const similarProfileId = similarProfileIdState;

  const { data: similarMatchesData, isLoading: isSimilarMatchesLoading } =
    useSimiliarMatches(
      {
        page: pagination,
        limit: 8,
        see_all: true,
        ...(similarProfileId ? { profile_id: similarProfileId } : {}),
      },
      {
        enabled: tabActive === "6" || (tabActive === "7" && selectedList === 4),
      },
    );
  useEffect(() => {
    if (
      isPremiumLoading ||
      isMyMatchesLoading ||
      isNearMeLoading ||
      islatestLoading ||
      isRecentLoading ||
      careerMatchesLoading ||
      professionalMatchesLoading ||
      isSimilarMatchesLoading
    ) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    isPremiumLoading,
    isMyMatchesLoading,
    isNearMeLoading,
    islatestLoading,
    isRecentLoading,
    careerMatchesLoading,
    professionalMatchesLoading,
    isSimilarMatchesLoading,
  ]);

  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem(
      "profileListScrollPosition",
    );
    const savedPath = sessionStorage.getItem("profileListPath");

    if (savedScrollPosition && savedPath === location.pathname) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: parseInt(savedScrollPosition),
          behavior: "smooth",
        });
      });

      sessionStorage.removeItem("profileListScrollPosition");
      sessionStorage.removeItem("profileListPath");
    }
  }, [location.pathname]);

  const apiResponse =
    selectedList === 1
      ? recent
      : selectedList === 2
        ? careerMatchesData
        : selectedList === 3
          ? professionalMatchesData
          : selectedList === 4
            ? similarMatchesData
            : {};

  const listData =
    apiResponse?.professional_matches ||
    apiResponse?.career_matches ||
    apiResponse?.recently_viewed ||
    apiResponse?.users_data ||
    [];
  const paginationDetails = apiResponse?.pagination_details || {
    count: 0,
    page: 1,
    limit: 0,
  };

  const tabItems = [
    {
      key: "1",
      label: (
        <span className="flex items-center gap-1-5">
          <ExploreMyMatches />
          My matches
        </span>
      ),
      children: (
        <ExploreLayout
          title="My matches"
          description="Personalized matches based on your preferences."
          paginationDetails={myMatchesData?.pagination_details}
        >
          <CardLists
            setPagination={setPagination}
            paginationDetails={myMatchesData?.pagination_details}
            data={myMatchesData?.users_data || []}
            selectedList={selectedList}
          />
        </ExploreLayout>
      ),
    },
    {
      key: "2",
      label: (
        <span className="flex items-center gap-1-5">
          <ExploreNearMe />
          Near me
        </span>
      ),
      children: (
        <ExploreLayout
          title="Near me"
          description="Personalized matches based on your preferences."
          paginationDetails={nearMeMatches?.pagination_details}
        >
          <CardLists
            setPagination={setPagination}
            paginationDetails={nearMeMatches?.pagination_details}
            data={nearMeMatches?.users_data || []}
            selectedList={selectedList}
          />
        </ExploreLayout>
      ),
    },
    {
      key: "3",
      label: (
        <span className="flex items-center gap-1-5">
          <ExploreLatest />
          Latest profiles
        </span>
      ),
      children: (
        <ExploreLayout
          title="Latest profiles"
          description="Personalized matches based on your preferences."
          paginationDetails={latest?.pagination_details}
        >
          <CardLists
            setPagination={setPagination}
            paginationDetails={latest?.pagination_details}
            data={latest?.users_data || []}
            selectedList={selectedList}
          />
        </ExploreLayout>
      ),
    },
    {
      key: "4",
      label: (
        <span className="flex items-center gap-1-5">
          <ExplorePremium />
          Premium profiles
        </span>
      ),
      children: (
        <ExploreLayout
          title="Premium profiles"
          description="Personalized matches based on your preferences."
          paginationDetails={premiumMatches?.pagination_details}
        >
          <CardLists
            setPagination={setPagination}
            paginationDetails={premiumMatches?.pagination_details}
            data={premiumMatches?.users_data || []}
            selectedList={selectedList}
          />
        </ExploreLayout>
      ),
    },
    {
      key: "5",
      label: (
        <span className="flex items-center gap-1-5">
          <ExploreRecommended />
          Recommended
        </span>
      ),
      children: (
        <ExploreLayout
          title="Recommended"
          description="Personalized matches based on your preferences."
          paginationDetails={myMatchesData?.pagination_details}
        >
          <CardLists
            setPagination={setPagination}
            paginationDetails={myMatchesData?.pagination_details}
            data={myMatchesData?.users_data || []}
            selectedList={selectedList}
          />
        </ExploreLayout>
      ),
    },
    {
      key: "6",
      label: (
        <span className="flex items-center gap-1-5">
          <ExploreMoreMatches />
          More Matches
        </span>
      ),
      children:
        tabActive === "7" ? (
          <ExploreLayout
            title={selectedListData[selectedList - 1]?.title}
            description={selectedListData[selectedList - 1]?.desc}
            count={paginationDetails?.count}
            isListing
          >
            <PaginationCard
              count={paginationDetails}
              isListing
              data={listData}
              current={pagination}
              setCurrent={setPagination}
              tabVariant="7"
              activeTab={selectedList.toString()}
              selectedList={selectedList}
              isLoading={
                selectedList === 1
                  ? isRecentLoading
                  : selectedList === 2
                    ? careerMatchesLoading
                    : selectedList === 3
                      ? professionalMatchesLoading
                      : isSimilarMatchesLoading
              }
            />
          </ExploreLayout>
        ) : (
          <MoreMatches />
        ),
    },
  ];

  return (
    <>
      <div className="container pb-8 pt-12 explore-page">
        <Tabs
          activeKey={tabActive === "7" ? "6" : tabActive}
          className="explore-tab"
          destroyInactiveTabPane={true}
          onChange={(key) => {
            setTabActive(key);
            clearAll();
          }}
          variant="primary"
          position="left"
          items={tabItems}
          extraContent={{
            left: isFilterVisible ? (
              <Button
                variant="custom"
                className="filter-icon"
                onClick={() => setShowFilter((prev) => !prev)}
              >
                <FilterIcon />
              </Button>
            ) : null,
            right: isFilterVisible ? (
              <Filter show={showFilter} setShowFilter={setShowFilter} />
            ) : null,
          }}
        />
      </div>
      <GoPremiumModal
        open={upgradeModal}
        wrapClassName={`go-premium-primary`}
        contentTitle="Want to see profiles that are interested in you?"
        buttonTitle="Upgrade plan"
        onCancel={() => {
          setUpgradeModal(false);
          storeToken("gopremiummodal", "true");
        }}
        onOk={() => {
          setUpgradeModal(false);
          storeToken("gopremiummodal", "true");
        }}
      />
    </>
  );
}

export default Explore;
